import { ReactNode, useEffect, useState } from 'react';
import { QrReader } from 'react-qr-reader';
import { IoReload } from 'react-icons/io5';

import Modal from 'legacy/common/components/modal/Modal';
import VerificationPopUp from './VerificationPopup';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import ManageEntryModal from './ManageEntryModal';

enum QRCodeStatus {
  IDLE = 'IDLE',
  READY = 'READY',
  VALID = 'VALID',
  INVALID = 'INVALID'
}

type OverlayProps = {
  qrCodeStatus: string;
  onClick: () => void;
};

const Overlay: React.FC<OverlayProps> = ({ qrCodeStatus, onClick }) => {
  let bgColor = 'bg-primary-500';

  if (qrCodeStatus === QRCodeStatus.VALID) {
    bgColor = 'bg-green-500';
  }

  if (qrCodeStatus === QRCodeStatus.INVALID) {
    bgColor = 'bg-red-500';
  }

  return (
    <div
      className="absolute h-full w-full top-0 left-0 flex flex-col items-center justify-center"
      onClick={onClick}
    >
      <div className={`w-full h-full bg-opacity-50 ${bgColor} `} />
      <div className="w-full h-[86vw] flex items-center justify-center flex-shrink-0">
        <div className={`w-full h-full bg-opacity-50 ${bgColor}`} />
        <div
          className="m-4 w-[80vw] h-[80vw] flex-shrink-0"
          style={{
            // Generated from https://kovart.github.io/dashed-border-generator/
            backgroundImage: `url("/img/qr/scan-border.svg")`,
            borderRadius: '22px'
          }}
        />
        <div className={`w-full h-full bg-opacity-50 ${bgColor}`} />
      </div>
      <div className={`w-full h-full bg-opacity-50 ${bgColor}`} />

      <div className="absolute w-full h-full bg-opacity-20 bg-black" />
    </div>
  );
};

const ErrorWrapper: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="absolute top-1/2 -translate-y-10 left-0 w-full text-center text-red-500 text-xl px-20">
    {children}
  </div>
);

const renderError = (error: string) => {
  // Possible exceptions: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
  if (error === 'NotAllowedError') {
    return (
      <ErrorWrapper>
        To grant access you need to refresh the page and try again.
        <div
          className="mt-12 flex items-center justify-center text-2xl text-white cursor-pointer font-normal"
          onClick={() => {
            window.location.reload();
          }}
        >
          <IoReload className="mr-2" />
          Refresh Now
        </div>
      </ErrorWrapper>
    );
  }

  return (
    <ErrorWrapper>
      There is an error with your camera. Error: {error}
    </ErrorWrapper>
  );
};

type ScanQRModalProps = {
  eventId: string;
  onClose: () => void;
};

const ScanQRModal: React.FC<ScanQRModalProps> = ({ eventId, onClose }) => {
  const [scannedResult, setScannedResult] = useState(null);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [qrCodeStatus, setQrCodeStatus] = useState(QRCodeStatus.IDLE);
  const [error, setError] = useState(null);

  const [showManageEntry, setShowManageEntry] = useState(false);
  const toggleManageEntry = () => {
    setShowManageEntry((showManageEntry) => !showManageEntry);
  };

  // Toggle verification popup
  // - Starts verification when scannedResult is different from previous QR code
  useEffect(() => {
    if (!scannedResult) {
      return;
    }

    setShowVerificationPopup(true);
  }, [scannedResult]);

  // Pause the video during QR verification
  useEffect(() => {
    const video = document.querySelector<HTMLVideoElement>('#qr-reader');
    if (!video) {
      return;
    }

    if (showVerificationPopup) {
      video.pause();
      return;
    }

    video.play();
  }, [showVerificationPopup]);

  return (
    <>
      <Modal visible onClose={onClose} isBehind={showManageEntry}>
        <div
          className="relative w-full h-full bg-black"
          data-testid="modal-scan-qr"
        >
          <QrReader
            videoId="qr-reader"
            scanDelay={0}
            containerStyle={{ width: '100%', height: '100vh' }}
            constraints={{ facingMode: 'environment' }}
            videoContainerStyle={{
              width: '100%',
              height: '100vh'
            }}
            videoStyle={{
              width: '100%',
              height: '100vh',
              objectFit: 'cover'
            }}
            onResult={(result, error) => {
              // react-qr-reader seems to always return 'e' as the error, even when there is no error
              if (error?.name && error.name !== 'e') {
                setError(error.name);
                return;
              }

              const scannedResult = result?.getText();

              if (!scannedResult) {
                return;
              }

              setScannedResult(scannedResult);
            }}
          />

          <Overlay
            qrCodeStatus={qrCodeStatus}
            onClick={() => {
              // Reset scanned result to scan the same QR code again
              setScannedResult(null);
            }}
          />

          <h1 className="w-full absolute left-0 top-0 p-12 text-center font-normal text-xl text-white ">
            Scan Ticket
          </h1>

          <div className="absolute top-28 w-full">
            <div className="w-40 mx-auto">
              <Button type={ButtonType.Primary} onClick={toggleManageEntry}>
                Manage Entry
              </Button>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 w-full p-10">
            <Button
              type={ButtonType.Outline}
              onClick={onClose}
              testId="button-close-scan-qr"
            >
              Cancel
            </Button>
          </div>

          {showVerificationPopup && (
            <VerificationPopUp
              eventId={eventId}
              qrCodeString={scannedResult}
              onSuccess={(data) => {
                if (data.hasAlreadyScanned) {
                  setQrCodeStatus(QRCodeStatus.IDLE);
                } else {
                  setQrCodeStatus(QRCodeStatus.VALID);
                }
              }}
              onError={() => {
                setQrCodeStatus(QRCodeStatus.INVALID);
              }}
              onClose={() => {
                setQrCodeStatus(QRCodeStatus.IDLE);
                setShowVerificationPopup(false);
              }}
            />
          )}

          {error && renderError(error)}
        </div>
      </Modal>

      {showManageEntry && (
        <ManageEntryModal onClose={toggleManageEntry} eventId={eventId} />
      )}
    </>
  );
};

export default ScanQRModal;
